import {
    type ComponentType,
    useEffect,
    Children,
    useMemo,
    type FC
} from "react";
import {
    Animated
} from "react-native";
import type ICarouselContentProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import type {
    CarouselPartContentProps
} from "../../type";
import {
    CarouselItemContext,
    withCarouselContext
} from "../../context";
import Skeleton from "../../../skeleton";

const CarouselContentContent: FC<CarouselPartContentProps<ICarouselContentProps>> = ({
    customTheme: customThemeProp,
    placeholder,
    children,
    context,
    style,
    ...props
}) => {
    const items = useMemo(() => Children.toArray(children), [
        children
    ]);

    const {
        container: containerDynamicStyle
    } = useStyles({
        orientation: context.orientation,
        isMeasured: context.isMeasured,
        metrics: context.metrics,
        gap: context.gap
    });

    useEffect(() => {
        context.handleCount(context.isLoading ? 0 : items.length);
    }, [
        context.isLoading,
        items.length
    ]);

    const translate = context.position.interpolate({
        outputRange: [
            0,
            context.direction === "rtl" && context.orientation === "horizontal" ? 1 : -1
        ],
        inputRange: [
            0,
            1
        ]
    });

    const renderItems = () => {
        if(context.isLoading) {
            return placeholder ?? <Skeleton
                customTheme={customThemeProp ?? context.customTheme}
                aspectRatio={16 / 9}
            />;
        }

        return items.map((item, index) => <CarouselItemContext.Provider
            value={index}
            key={index}
        >
            {item}
        </CarouselItemContext.Provider>);
    };

    return <Animated.View
        {...props}
        {...context.panHandlers}
        style={[
            style,
            context.orientation === "vertical" ? stylesheet.vertical : stylesheet.horizontal,
            containerDynamicStyle,
            {
                transform: [
                    context.orientation === "vertical" ? {
                        translateY: translate
                    } : {
                        translateX: translate
                    }
                ]
            }
        ]}
        aria-live={context.isAutoplayPlaying ? "off" : "polite"}
    >
        {renderItems()}
    </Animated.View>;
};

const CarouselContent: ComponentType<ICarouselContentProps> = withCarouselContext("content", CarouselContentContent);
export default CarouselContent;
